import { useCallback, useEffect } from 'react';
import { useOnboardingTasks } from 'components/onboarding-checklist/useOnboardingTasks';
import { useRecommendedActionsData } from 'components/recommended-actions/useRecommendedActionsData';
import { useChecklistData } from 'components/required-checklist/useChecklistData';
import { useRequiredChecklistVisibility } from 'components/required-checklist/useRequiredChecklistVisibility';
import { posthogEvents } from 'constants/posthog-events';
import { usePosthog } from 'customHooks/usePosthog';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface IDashboardViewedProps {
    sections: {
        checklist: boolean;
        metrics: boolean;
        recommendedActions: boolean;
        profilePanel: boolean;
        calloutCards: boolean;
    };
    checklistItems?: string[];
    recommendedItems?: { key: string; title: string; position: number }[];
}

const usePersonalizedDashboardPosthog = () => {
    const { pushEvent } = usePosthog();
    const {
        showRequiredChecklist = false,
        showRecommendedActions = false,
        personalizedDashboard = false,
        mobileAppRelease = false,
        profilePanel = false,
    } = useFlags();
    const { actionData } = useRecommendedActionsData();
    const { tasks } = useOnboardingTasks();
    const { checklist } = useChecklistData();
    const { shouldShowChecklists } = useRequiredChecklistVisibility();

    const registerDashboardView = useCallback(() => {
        const remainingActionsWithPosition = actionData
            .filter(action => !action.isComplete)
            .map((action, index) => {
                return {
                    key: action.actionKey,
                    title: action.title,
                    position: index,
                };
            });

        const eventProperties: IDashboardViewedProps = {
            sections: {
                checklist: showRequiredChecklist && shouldShowChecklists,
                metrics: !!tasks?.hasRaisedFirstDollar && !shouldShowChecklists,
                recommendedActions:
                    showRecommendedActions && remainingActionsWithPosition.length > 0,
                profilePanel: !!tasks?.hasRaisedFirstDollar && profilePanel,
                calloutCards: !showRecommendedActions && mobileAppRelease,
            },
        };

        if (showRecommendedActions && remainingActionsWithPosition.length > 0) {
            eventProperties.recommendedItems = remainingActionsWithPosition;
        }

        if (showRequiredChecklist && shouldShowChecklists) {
            eventProperties.checklistItems = Object.keys(checklist);
        }

        pushEvent(posthogEvents.DASHBOARD_VIEWED, eventProperties);
    }, [
        mobileAppRelease,
        showRecommendedActions,
        actionData,
        showRequiredChecklist,
        shouldShowChecklists,
        checklist,
        profilePanel,
        pushEvent,
        tasks?.hasRaisedFirstDollar,
    ]);

    useEffect(() => {
        personalizedDashboard && registerDashboardView();
    }, [registerDashboardView, personalizedDashboard]);
};

export { usePersonalizedDashboardPosthog };
